/** Privacy Policy body (title and date rendered by LegalPageLayout). */
export const PRIVACY_POLICY_BODY = `
This Privacy Policy applies to the website, platform, products, applications, and services operated under Aivory EMS, an intellectual property, product, and service offering owned and operated by AivoryLab Pte. Ltd. (“AivoryLab”, “Aivory EMS”, “Company”, “we”, “our”, or “us”).

AivoryLab Pte. Ltd. is a company incorporated in Singapore.

By accessing or using Aivory EMS, you acknowledge that you have read, understood, and agreed to this Privacy Policy.

---

# 1. Company Information

## Legal Entity

Aivory EMS is a proprietary software platform and intellectual property owned, developed, licensed, and operated by:

**AivoryLab Pte. Ltd.**
Singapore

Website: [https://aivoryems.com](https://aivoryems.com)

For privacy or legal matters, contact:
[privacy@aivorylab.com](mailto:privacy@aivorylab.com)

---

# 2. Scope of This Policy

This Privacy Policy explains how AivoryLab Pte. Ltd. collects, uses, stores, processes, transfers, and protects personal data when users:

* visit our website
* request demonstrations
* register for events
* use our event management platform
* access virtual or hybrid event services
* interact with event organizers using Aivory EMS
* communicate with us
* use related products and integrations

This Privacy Policy applies to:

* attendees
* exhibitors
* sponsors
* organizers
* partners
* customers
* website visitors
* platform users

---

# 3. Information We Collect

## A. Information Provided Directly

We may collect personal data that users voluntarily provide, including:

* full name
* email address
* mobile number
* company name
* organization details
* job title
* billing information
* account credentials
* profile information
* uploaded content
* event registration details
* survey responses
* communications submitted to us

---

## B. Event Participation Data

When users participate in events powered by Aivory EMS, we may process:

* attendee registrations
* ticket purchases
* session selections
* attendance records
* badge information
* QR code activity
* networking interactions
* exhibitor engagement
* sponsor interactions
* onsite check-in records
* engagement analytics
* feedback submissions

---

## C. Automatically Collected Information

We may automatically collect technical and usage-related information, including:

* IP address
* browser type
* device identifiers
* operating system
* pages visited
* access timestamps
* clickstream activity
* referring URLs
* cookies and tracking data
* session activity logs

---

## D. Third-Party Integrations

Where integrations are enabled, we may receive data from third-party systems including:

* CRM systems
* payment gateways
* marketing platforms
* analytics providers
* authentication providers (including Google and Apple when you choose “Sign in with Google” or “Sign in with Apple” for operator accounts)
* video conferencing platforms
* ticketing systems
* communication tools

---

# 4. Purpose of Processing

We process personal data for legitimate business and operational purposes, including:

## Service Delivery

* operating the Aivory EMS platform
* facilitating registrations and ticketing
* managing attendee access
* delivering hybrid and virtual event experiences
* enabling networking and engagement features
* providing customer support

## Platform Operations

* maintaining platform security
* improving system performance
* troubleshooting technical issues
* monitoring system reliability
* preventing fraud and abuse

## Business Communications

* responding to inquiries
* providing event updates
* sending confirmations and notifications
* communicating service-related information

## Marketing & Promotional Activities

Subject to applicable laws, we may send:

* newsletters
* product announcements
* marketing communications
* event invitations
* promotional campaigns

Users may opt out of marketing communications at any time.

## Legal & Regulatory Compliance

* complying with legal obligations
* enforcing contractual rights
* protecting legal interests
* responding to lawful governmental requests

---

# 5. Legal Basis for Processing

Where applicable under relevant privacy laws, we process personal data based on:

* contractual necessity
* legitimate interests
* user consent
* legal obligations
* protection of vital interests

---

# 6. Cookies & Tracking Technologies

Aivory EMS uses cookies, analytics tools, and related technologies to:

* maintain user sessions
* remember preferences
* improve user experience
* analyze traffic and usage
* optimize platform performance
* support security monitoring

Users may manage cookie preferences through browser settings.

Disabling cookies may affect platform functionality.

---

# 7. Sharing & Disclosure of Information

AivoryLab Pte. Ltd. does not sell personal data.

We may disclose personal data under the following circumstances:

## A. Event Organizers

Where users participate in events powered by Aivory EMS, relevant information may be shared with the respective event organizer.

Event organizers are independently responsible for their own data handling practices.

---

## B. Service Providers & Vendors

We may share information with authorized third-party service providers assisting in:

* cloud hosting
* payment processing
* analytics
* customer support
* communications
* infrastructure operations
* cybersecurity

Such providers are contractually required to protect personal data.

---

## C. Legal & Regulatory Requirements

We may disclose information where required by:

* applicable law
* regulatory authority
* court order
* legal process
* governmental request

or where necessary to:

* protect legal rights
* investigate fraud
* maintain platform security
* enforce agreements

---

## D. Corporate Transactions

Personal data may be transferred in connection with:

* mergers
* acquisitions
* investments
* restructuring
* sale of business assets

subject to appropriate confidentiality protections.

---

# 8. International Data Transfers

Personal data may be processed or stored in jurisdictions outside Singapore.

Where international transfers occur, AivoryLab Pte. Ltd. implements commercially reasonable safeguards and contractual protections consistent with applicable data protection laws.

---

# 9. Artificial Intelligence (AivoryOne)

Aivory EMS includes optional AI features branded as **AivoryOne**. When you use AivoryOne:

* We process prompts, conversation history, tool actions, and relevant workspace context you authorize the AI to access (for example event details, form drafts, or pasted agenda text).
* Prompts and AI responses are stored in your tenant workspace for continuity, auditability, and support. Retention follows the same operational periods as other platform logs unless a shorter contractual period applies.
* We apply technical controls including tenant isolation, human confirmation for high-impact actions (publish/import/bulk), secret redaction before model calls, prompt-injection defenses, and output safety filtering.
* We do **not** use your customer content to train shared public foundation models without your explicit written consent.
* Model inference may be performed by subprocessors (for example hosted inference providers). Where used, they process data under contract and only for providing the AI feature.
* Singapore is the default hosting region for production Aivory EMS. Enterprise customers may request regional residency options (Singapore, Malaysia, Australia) subject to commercial terms.

Operators remain responsible for not pasting secrets or unnecessary personal data into AI prompts.

---

# 10. Data Retention

We retain personal data only for as long as reasonably necessary for:

* operational purposes
* contractual obligations
* compliance requirements
* dispute resolution
* legal enforcement
* security and audit purposes

Retention periods may vary depending on:

* event organizer instructions
* regulatory obligations
* contractual requirements
* platform usage activity

---

# 11. Data Security

AivoryLab Pte. Ltd. implements reasonable administrative, technical, and organizational safeguards designed to protect personal data, including:

* access controls
* authentication mechanisms
* encrypted communications
* infrastructure monitoring
* security logging
* restricted administrative access
* secure hosting environments

However, no method of electronic transmission or storage is completely secure.

Accordingly, we do not guarantee absolute security or uninterrupted operation of the Services.

---

# 12. User Rights

Depending on applicable laws, users may have rights to:

* access personal data
* correct inaccurate information
* request deletion
* restrict processing
* object to processing
* withdraw consent
* request data portability

Requests may be submitted to:
[privacy@aivoryems.com](mailto:privacy@aivoryems.com)

We may require verification of identity before processing requests.

---

# 13. Third-Party Websites & Services

The Services may contain links to third-party websites, applications, or services.

AivoryLab Pte. Ltd. is not responsible for the privacy practices, content, or security of third-party services.

Users should review the applicable policies of those third parties.

---

# 14. Children’s Privacy

Aivory EMS is not intended for children under the minimum age required under applicable laws.

We do not knowingly collect personal data from children without appropriate authorization.

If we become aware that such data has been collected improperly, we may take steps to delete it.

---

# 15. Intellectual Property Notice

Aivory EMS, including its software, branding, workflows, designs, content, user interfaces, and related technologies, constitutes proprietary intellectual property owned or licensed by AivoryLab Pte. Ltd.

Nothing in this Privacy Policy transfers ownership rights to users.

Unauthorized copying, reproduction, reverse engineering, or distribution of the platform or related materials may violate intellectual property laws and contractual obligations.

---

# 16. Limitation of Liability

To the maximum extent permitted by applicable law, AivoryLab Pte. Ltd. shall not be liable for:

* indirect damages
* incidental damages
* consequential damages
* business interruption
* loss of profits
* loss of data
* unauthorized access beyond reasonable control

arising from the use of the Services.

Nothing in this Privacy Policy excludes liability that cannot legally be excluded under applicable law.

---

# 17. Changes to This Privacy Policy

AivoryLab Pte. Ltd. reserves the right to modify or update this Privacy Policy at any time.

Updated versions will be published on the website with a revised effective date.

Continued use of the Services following updates constitutes acceptance of the revised Privacy Policy.

---

# 18. Governing Law

This Privacy Policy shall be governed by and construed in accordance with the laws of Singapore.

Any disputes arising in connection with this Privacy Policy shall be subject to the exclusive jurisdiction of the courts of Singapore.

---

# 19. Singapore PDPA Compliance

AivoryLab Pte. Ltd. aims to comply with the Singapore Personal Data Protection Act 2012 (“PDPA”) regarding the collection, use, disclosure, retention, and protection of personal data.

---

# 20. GDPR & International Compliance

Where applicable, users located in the European Economic Area (“EEA”), United Kingdom, or other regulated jurisdictions may exercise rights provided under applicable data protection laws, including the General Data Protection Regulation (“GDPR”).

---

# 21. Contact Information

For legal, compliance, or privacy-related matters, please contact:

## AivoryLab Pte. Ltd.

Website:
[https://aivoryems.com](https://aivoryems.com)

Email:
[privacy@aivorylab.com](mailto:privacy@aivorylab.com)

Singapore
`.trim();
